import { Component, effect, ChangeDetectionStrategy } from '@angular/core';
import { AttributeComponent } from '@xanadu/core';
import { Register } from '@xanadu/core';
import { Genus, Preview, ImportData, Prototype, PublishData, AspectCheck } from '@xanadu/model';
import { Observable, of } from 'rxjs';

@Component({
    selector: 'app-genus-panel',
    templateUrl: './genus-panel.component.html',
    styleUrls: ['./genus-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@Register('genus-panel')
export class GenusPanelComponent extends AttributeComponent<Genus> {
    tabGenus = { caption: 'Genus', active: true, hidden: false };
    tabAttribute = { caption: 'Attribute', active: false, hidden: false };
    tabPreview = { caption: 'Preview', active: false, hidden: false };
    tabPublish = { caption: 'Publish', active: false, hidden: false };
    tabImport = { caption: 'Import', active: false, hidden: false };

    metaGenus: any = { id: 'prototypeId', name: 'Prototype', info: 'Genus prototype', max: 1, mandatory: true };
    metaAspect: any = { id: 'aspects', name: 'Aspects', info: 'Genus aspects' };
    metaAttribute: any = { id: 'attributes', name: 'Attributes', info: 'Genus attributes' };
    metaGraphql: any = { id: 'graphqlType', name: 'GraphQL Type', info: 'GraphQL type name, starts with a letter, can contain letters, numbers and underscores only.', pattern: '^[A-Za-z][a-zA-Z0-9_]*$', mandatory: true, updateOn: 'blur' }; //id can NOT be 'key', it is a preserved word

    enableEndpoint: boolean = false;
    enableCast: boolean = false;
    enableImport: boolean = false;
    graphqlValue?: string = undefined;
    exclusives?: string[];

    private importData: any;
    private aspectCheck?: AspectCheck;

    constructor() {
        super();

        effect(() => {
            const  root = this.treeSignal.contentRoot();
            const prototypeId = (<Genus>root?.node)?.prototypeId;
            if (root && prototypeId) {
                this.exclusives = [prototypeId];
            }
        });

        effect(() => {
            const endpoint = this.treeSignal.importEndpoint();
            this.enableEndpoint = !!endpoint;
        });

        effect(() => {
            const cast = this.treeSignal.importCast();
            const response = this.treeSignal.importResponse();
            this.enableCast = !!cast && !!response;
        });

        effect(() => {
            const result = this.treeSignal.importResult();
            this.importData = result;
            this.enableImport = !!result;
        });
    }

    onPick(prototype: Prototype): void {
        if (prototype) {
            const genus = this.node();
            if (genus && prototype._id) {
                genus.prototypeId = prototype._id;
                genus.prototype = prototype;
            }
        }
    }

    onDelete(prototype: Prototype): void {
        const genus = this.node();
        if (genus) {
            delete genus.graphqlType;
            delete genus.prototypeId;
            delete genus.prototype;
        }
    }

    onRequest() {
        this.treeSignal.importRequest.set({});
    }

    onConvert() {
        this.treeSignal.importConvert.set({});
    }

    onImport() {
        if (this.importData) {
            if (this.importData instanceof Array) {
                this.treeSignal.importMany.set(this.importData);
            } else {
                this.treeSignal.import.set(this.importData);
            }
        }
    }

    onPermission(): void {
        const genus = this.node();
        if (genus) {
            this.doPermission(Genus, genus);
        }
    }

    onCascade(): void {
        this.doCascade(Genus);
    }

    onAdmission(): void {
        const genus = this.node();
        if (genus) {
            this.doAdmission(Genus, genus);
        }
    }

    validate: (x: any) => Observable<boolean> = (value) => {
        if (this.graphqlValue !== value) {
            this.graphqlValue = value;
            const genus = this.node();
            if (genus?._id) {
                return this.dataService.checkGraphQL(genus._id, value, genus.siteId);
            }
        }

        return of(true);
    }

    protected override setup(genus: Genus): void {
        super.setup(genus);

        const model = this.model();

        this.metaGenus.disabled = !!genus?.nodes.length || !!genus?.branches.length;

        this.tabs = genus?.parentId?.length ? [this.tabGenus, this.tabAttribute, this.tabPreview, this.tabImport, this.tabPublish] : [];

        if (this.permissible) {
            this.tabs.push(this.tabSecurity);
            if (genus?.parentId?.length) {
                this.tabs.push(this.tabAccess);
            }
        }

        this.tabSelected = this.tabs[0];
        model.custome._import = model.custome._import || {};

        if (genus) {
            this.aspectCheck = new AspectCheck(genus);
        }
    }

    protected override load(genus: Genus, form: any): string | undefined {
        super.load(genus, form);

        const { _import, publish, prototypeId, graphqlType, aspects, attributes, preview, ...rest } = form;

        if (genus) {
            genus.publish = new PublishData(publish);
            genus.graphqlType = graphqlType;
            genus.import = new ImportData(_import);

            if (preview) {
                genus.preview = new Preview(preview);
            }

            if (genus.prototypeId) {
                this.aspectCheck?.check(aspects, attributes);
                genus.aspects = aspects;
                genus.attributes = attributes;
            }
        }

        return;
    }

    protected override refresh(genus: Genus): void {
        super.refresh(genus);

        this.aspectCheck?.applyDelta();
    }
}
